// Zentrale Konfiguration für den Nachhaltigkeitspräferenz-Assistenten.
// Änderungen an Fragen, Optionen, Erklärtexten oder PDF-Texten NUR hier vornehmen.
// Stand: September 2026.

export const CONFIG = {
  meta: {
    stand: "September 2026",
    version: "1.0",
    // Für PDF und für "Stand"-Hinweis im UI:
    displayStand: "Stand: September 2026. Die Regeln können sich mit der SFDR-Reform ändern.",
    // Datumsformat: JJJJ-MM-TT für Dateinamen, TT.MM.JJJJ fürs PDF-Dokument selbst
  },

  disclaimer:
    "Dieses Werkzeug bereitet Ihr Beratungsgespräch vor. Es ersetzt weder eine Anlageberatung noch eine rechtliche oder steuerliche Beratung und enthält keine Empfehlung für bestimmte Produkte. Die rechtlich maßgebliche Erfassung Ihrer Nachhaltigkeitspräferenzen erfolgt durch Ihre Bank oder Ihren Berater. Die Regeln können sich mit der Überarbeitung der EU-Offenlegungsverordnung ändern.",

  privacy:
    "Ihre Angaben werden ausschließlich in Ihrem Browser verarbeitet. Wir speichern und übermitteln sie nicht.",

  intro: {
    title: "Nachhaltigkeitspräferenz-Assistent",
    subtitle: "Vorbereitung auf das Beratungsgespräch",
    lead:
      "Seit dem 2. August 2022 sind Banken und Anlageberater verpflichtet, im Beratungsgespräch Ihre Nachhaltigkeitspräferenz abzufragen. Mit diesem Werkzeug klären Sie die Fragen vorab in Ruhe. Sie erhalten am Ende eine Zusammenfassung und einen PDF-Ausdruck, den Sie mit ins Gespräch nehmen können.",
    bullets: [
      "Rund 5 Minuten für alle Fragen.",
      "Ihre Angaben bleiben auf Ihrem Gerät. Wir speichern nichts.",
      "Keine Produktempfehlung, keine Anlageberatung.",
    ],
    ctaLabel: "Los geht's (ca. 5 Minuten)",
  },

  // Ergänzende Werte (nicht Teil der drei gesetzlichen Wege)
  ergaenzendeWerte: [
    { key: "waffen", label: "Rüstung / sämtliche Waffen" },
    { key: "kohle", label: "Kohle und fossile Brennstoffe" },
    { key: "atom", label: "Atomenergie" },
    { key: "tabak", label: "Tabak" },
    { key: "gluecksspiel", label: "Glücksspiel" },
    { key: "alkohol", label: "Alkohol" },
    { key: "porno", label: "Pornografie" },
    { key: "tierversuche", label: "Tierversuche" },
    { key: "gentechnik", label: "Gentechnik" },
  ],

  paiThemen: [
    { key: "thg", label: "Treibhausgasemissionen und Klima" },
    { key: "bio", label: "Biodiversität" },
    { key: "wasser", label: "Wasser" },
    { key: "abfall", label: "Abfall" },
    { key: "soziales", label: "Soziales und Beschäftigung" },
    { key: "menschenrechte", label: "Menschenrechte" },
    { key: "korruption", label: "Korruption und Bestechung" },
    { key: "kontrWaffen", label: "Kontroverse Waffen" },
    { key: "alle", label: "Alle wichtigen Themen" },
  ],

  // Kernfragen an den Berater – fest, unabhängig von Auswahl
  kernfragen: [
    "Welche Produkte erfüllen meine Vorgaben und auf welcher Datengrundlage?",
    "Wie hoch ist der tatsächliche Anteil taxonomiekonformer bzw. nachhaltiger Investitionen und wie wird er berechnet?",
    "Welche PAI-Indikatoren berücksichtigt das Produkt konkret?",
    "Welche Ausschlüsse gelten und mit welchen Umsatzschwellen?",
    "Was sagt die Einstufung als Artikel 8 oder Artikel 9 konkret über meine Vorgaben aus?",
    "Was passiert, wenn das Produkt meine Vorgaben später nicht mehr erfüllt?",
    "Wie hoch sind die Gesamtkosten im Vergleich zu Alternativen?",
    "Wie wird meine Präferenz dokumentiert und wann überprüft?",
    "Wie wird das Produkt voraussichtlich in die neuen EU-Kategorien (SFDR 2.0) eingeordnet?",
  ],

  // Zusatzfragen je nach Auswahl (Wert → Frage)
  themenfragen: {
    waffen: "Wie stellt das Produkt sicher, dass keine Umsätze mit Rüstungsgütern oder Waffen erzielt werden – und ab welcher Umsatzschwelle?",
    kohle: "Wie werden Unternehmen mit Kohle- und fossilem Geschäft (Umsatzanteil, Erweiterungspläne) im Portfolio behandelt?",
    atom: "Wie geht das Produkt mit Investitionen in Atomenergie um? (EU-Taxonomie enthält Kernkraft unter Bedingungen.)",
    tabak: "Sind Tabakproduzenten und -zulieferer vollständig ausgeschlossen?",
    gluecksspiel: "Werden Glücksspielunternehmen und Anbieter kritisch geprüft oder ausgeschlossen?",
    alkohol: "Werden Unternehmen mit wesentlichen Umsatzanteilen aus Alkohol ausgeschlossen?",
    porno: "Werden Unternehmen mit Umsätzen aus Pornografie ausgeschlossen?",
    tierversuche: "Wie werden Unternehmen mit nicht regulierten Tierversuchen behandelt?",
    gentechnik: "Wie wird der Einsatz von Gentechnik im Portfolio erfasst und bewertet?",
  },

  // "Gut zu wissen" – 5 kurze Punkte, keine neuen Rechtsbehauptungen
  gutZuWissen: [
    "Seit dem 2. August 2022 sind Banken und Anlageberater verpflichtet, im Gespräch Ihre Nachhaltigkeitspräferenz abzufragen.",
    "Es gibt drei gesetzliche Wege, Nachhaltigkeit festzulegen: EU-Taxonomie, nachhaltige Investitionen im Sinne der Offenlegungsverordnung (SFDR) und Berücksichtigung der wichtigsten nachteiligen Auswirkungen (PAI). Sie können einen, mehrere oder alle wählen.",
    "Eine reine Einstufung eines Fonds als Artikel 8 oder Artikel 9 reicht nicht aus, um Ihre Nachhaltigkeitspräferenz zu erfüllen.",
    "Persönliche Ausschlüsse (z. B. Waffen, Kohle) sind kein gesetzlicher Weg, aber ergänzend im Gespräch üblich.",
    "Ihre Präferenz kann jederzeit geändert werden. Die Regeln werden mit SFDR 2.0 voraussichtlich angepasst.",
  ],

  // Weiterführende Links auf der Ergebnisseite (nur vorhandene Zielseiten)
  weiterLinks: [
    { to: "/artikel/greenwashing-erkennen", label: "Greenwashing erkennen – Praxis-Leitfaden" },
    { to: "/artikel/sfdr-2-0-reform", label: "SFDR 2.0: Was sich mit der Reform ändert" },
    { to: "/nachhaltige-geldanlage-deutschland", label: "Marktreport Nachhaltige Geldanlage Deutschland" },
    { to: "/artikel/fng-marktbericht-2026", label: "FNG-Marktbericht 2026" },
    { to: "/artikel/esg-kriterien-einfach-erklaert", label: "ESG-Kriterien einfach erklärt" },
  ],

  faq: [
    {
      q: "Muss ich im Beratungsgespräch Nachhaltigkeitspräferenzen angeben?",
      a: "Ihre Bank oder Ihr Berater ist verpflichtet, danach zu fragen. Sie sind aber nicht verpflichtet, Nachhaltigkeit als Kriterium zu wählen. Sie können jederzeit „Nachhaltigkeit ist mir nicht wichtig“ angeben – und das später ändern.",
    },
    {
      q: "Kann ich meine Präferenz später ändern?",
      a: "Ja. Eine einmal geäußerte Präferenz ist nicht in Stein gemeißelt. Sprechen Sie Ihre Bank oder Ihren Berater an, um die Angaben in Ihrem Beratungsprotokoll zu aktualisieren.",
    },
    {
      q: "Was ist der Unterschied zwischen Artikel 8, Artikel 9 und Nachhaltigkeitspräferenz?",
      a: "Artikel 8 und 9 stammen aus der Offenlegungsverordnung (SFDR) und beschreiben, welche Nachhaltigkeitsmerkmale ein Fonds ausweist. Die Nachhaltigkeitspräferenz nach § 2 Nr. 7 Delegierter Verordnung (EU) 2017/565 ist enger: Sie verlangt konkrete Mindestanteile (Taxonomie, nachhaltige Investitionen) oder eine PAI-Berücksichtigung. Ein Artikel-8-Fonds erfüllt die Nachhaltigkeitspräferenz nicht automatisch.",
    },
    {
      q: "Was bedeutet PAI?",
      a: "PAI steht für „Principal Adverse Impacts“ – die wichtigsten nachteiligen Auswirkungen einer Investition auf Nachhaltigkeitsfaktoren. Dazu gehören unter anderem Treibhausgasemissionen, Biodiversitätsverlust, Wasserverbrauch, soziale Standards und Menschenrechte. Wie ein Produkt PAI konkret berücksichtigt, entscheidet der Anbieter.",
    },
    {
      q: "Werden meine Daten in diesem Werkzeug gespeichert?",
      a: "Nein. Alle Angaben werden ausschließlich in Ihrem Browser verarbeitet, wir übertragen nichts an einen Server. Wenn Sie die Seite schließen, sind die Angaben verloren – aber Sie können vorher jederzeit ein PDF herunterladen.",
    },
    {
      q: "Was ist die EU-Taxonomie?",
      a: "Ein einheitliches EU-Klassifikationssystem für ökologisch nachhaltige Wirtschaftstätigkeiten. Eine Aktivität gilt als taxonomiekonform, wenn sie zu einem von sechs Umweltzielen wesentlich beiträgt, keinem anderen erheblich schadet und Mindestschutz-Kriterien erfüllt. Bislang gilt nur ein kleiner Anteil der Wirtschaft als taxonomiekonform.",
    },
    {
      q: "Warum spielt die Taxonomie in nachhaltigen Publikumsfonds bisher eine kleine Rolle?",
      a: "Weil der Anteil taxonomiekonformer wirtschaftlicher Aktivitäten insgesamt gering ist. Laut FNG-Marktbericht 2026 kombinieren rund 77 % der erfassten nachhaltigen Publikumsfonds in Deutschland PAI-Berücksichtigung mit Angaben nach der Offenlegungsverordnung; die Taxonomie als eigenständiges Kriterium wird bislang selten quantifiziert ausgewiesen.",
    },
    {
      q: "Ändert sich mit SFDR 2.0 etwas an meiner Präferenz?",
      a: "Die geplante Reform ersetzt die bisherigen Kategorien voraussichtlich durch „Transition“, „ESG-Basic“ und „Sustainable“. Das kann Auswirkungen darauf haben, welche Produkte künftig als „nachhaltig“ gelten. Ihre einmal gemachten Angaben behalten aber ihre Aussagekraft; die Zuordnung zu Produkten kann sich ändern.",
    },
  ],
};

// Wizard-Schritte für Progress-Anzeige (0 = Intro, 9 = Ergebnis)
export const STEPS = [
  { key: "intro", label: "Start" },
  { key: "grundfrage", label: "Grundfrage" },
  { key: "erklaerung", label: "Drei Wege" },
  { key: "taxonomie", label: "EU-Taxonomie" },
  { key: "nachhaltige", label: "Nachhaltige Investitionen" },
  { key: "pai", label: "PAI" },
  { key: "werte", label: "Persönliche Werte" },
  { key: "flexibilitaet", label: "Flexibilität" },
  { key: "name", label: "Name & Datum" },
  { key: "ergebnis", label: "Ergebnis" },
];

// Ein-Klick-Profile: prefill + direkt zur Ergebnisseite. Die Werte sind
// bewusst konservativ formuliert und lassen sich nach dem Prefill
// jederzeit über „Neu starten" verwerfen.
export const PRESETS = [
  {
    key: "einsteiger",
    title: "Einsteiger",
    tagline: "Vorsichtig einsteigen, breite Ausschlüsse",
    description:
      "Für alle, die zum ersten Mal nachhaltig anlegen: moderate Mindestanteile, PAI ganzheitlich, klarer Waffenausschluss – Vorgaben werden im Gespräch angepasst, falls kein Produkt exakt passt.",
    build: () => ({
      grundfrage: "ja",
      taxonomie: { active: false, min: 25 },
      nachhaltige: { active: true, min: 10, schwerpunkt: "egal" },
      pai: { active: "ja", themen: ["alle"] },
      werte: { auswahl: ["waffen"], sonstiges: "" },
      flexibilitaet: "anpassen",
      name: "",
      datum: new Date().toISOString().slice(0, 10),
    }),
  },
  {
    key: "klimafokus",
    title: "Klimafokus",
    tagline: "Klarer Klima-Schwerpunkt, strikte Vorgaben",
    description:
      "Für Anleger mit klarem Klima-Fokus: Mindestanteil Taxonomie 25 %, 50 % nachhaltige Investitionen mit ökologischem Schwerpunkt, PAI auf Klima/Biodiversität/Wasser, harte Ausschlüsse (Waffen, Kohle, Atom). Lieber verzichten als lockern.",
    build: () => ({
      grundfrage: "ja",
      taxonomie: { active: true, min: 25 },
      nachhaltige: { active: true, min: 50, schwerpunkt: "oeko" },
      pai: { active: "ja", themen: ["thg", "bio", "wasser"] },
      werte: { auswahl: ["waffen", "kohle", "atom"], sonstiges: "" },
      flexibilitaet: "verzichten",
      name: "",
      datum: new Date().toISOString().slice(0, 10),
    }),
  },
  {
    key: "kompromiss",
    title: "Kompromissbereit",
    tagline: "Nachhaltig, aber pragmatisch",
    description:
      "Für Anleger, die Nachhaltigkeit wichtig finden, aber auf breite Produktauswahl nicht verzichten wollen: 25 % nachhaltige Investitionen, PAI auf Klima und Menschenrechte, Waffenausschluss. Vorgaben werden im Gespräch angepasst.",
    build: () => ({
      grundfrage: "ja",
      taxonomie: { active: false, min: 25 },
      nachhaltige: { active: true, min: 25, schwerpunkt: "egal" },
      pai: { active: "ja", themen: ["thg", "menschenrechte"] },
      werte: { auswahl: ["waffen"], sonstiges: "" },
      flexibilitaet: "anpassen",
      name: "",
      datum: new Date().toISOString().slice(0, 10),
    }),
  },
];

  // Initial-State
export const initialAnswers = () => ({
  grundfrage: null, // "ja" | "nein" | "unsicher"
  taxonomie: { active: false, min: 25 },
  nachhaltige: { active: false, min: 25, schwerpunkt: "egal" }, // egal | oeko | sozial
  pai: { active: null, themen: [] }, // active: "ja" | "nein" | "unsicher"
  werte: { auswahl: [], sonstiges: "" },
  flexibilitaet: null, // "anpassen" | "verzichten" | "unsicher"
  name: "",
  datum: new Date().toISOString().slice(0, 10), // yyyy-mm-dd
});

// -------- Musterabsatz-Generator (Ich-Form) --------
export function buildMusterabsatz(a) {
  if (a.grundfrage === "nein") {
    return "Ich möchte in meiner Anlage keine Nachhaltigkeitspräferenz berücksichtigen. Bitte dokumentieren Sie das ausdrücklich. Ich behalte mir vor, diese Angabe später zu ändern.";
  }
  if (a.grundfrage === "unsicher") {
    return "Ich möchte im Gespräch klären, welche Wege der Nachhaltigkeitspräferenz für mich sinnvoll sind. Bitte erläutern Sie mir die Optionen (EU-Taxonomie, nachhaltige Investitionen nach Offenlegungsverordnung, Berücksichtigung der wichtigsten nachteiligen Auswirkungen) und was sie in Ihren Produkten konkret bedeuten.";
  }
  const teile = [];
  teile.push("Ich möchte, dass bei meiner Geldanlage Nachhaltigkeitsaspekte berücksichtigt werden.");

  if (a.taxonomie.active) {
    teile.push(`Ich lege Wert darauf, dass mindestens ${a.taxonomie.min} % meines Investments in Wirtschaftstätigkeiten fließen, die nach der EU-Taxonomie als ökologisch nachhaltig gelten.`);
  }
  if (a.nachhaltige.active) {
    let sw = "";
    if (a.nachhaltige.schwerpunkt === "oeko") sw = " mit ökologischem Schwerpunkt";
    else if (a.nachhaltige.schwerpunkt === "sozial") sw = " mit sozialem Schwerpunkt";
    teile.push(`Zusätzlich soll mindestens ${a.nachhaltige.min} % in nachhaltige Investitionen im Sinne der Offenlegungsverordnung fließen${sw}.`);
  }
  if (a.pai.active === "ja") {
    const themen = a.pai.themen.includes("alle")
      ? "aller wichtigen Themen"
      : a.pai.themen.length
        ? `folgender Themen: ${a.pai.themen.map(mapPaiLabel).join(", ")}`
        : "der wichtigsten Themen";
    teile.push(`Ich möchte, dass die wichtigsten nachteiligen Auswirkungen auf Nachhaltigkeitsfaktoren (PAI) berücksichtigt werden, insbesondere im Bereich ${themen}.`);
  }

  const werteLabel = a.werte.auswahl.map((k) => CONFIG.ergaenzendeWerte.find((v) => v.key === k)?.label).filter(Boolean);
  if (werteLabel.length || a.werte.sonstiges) {
    const teil = werteLabel.length ? `keine Investitionen in ${werteLabel.join(", ")}` : "";
    const sonst = a.werte.sonstiges ? `${teil ? " und " : "keine Investitionen in "}${a.werte.sonstiges}` : "";
    teile.push(`Persönlich möchte ich möglichst ${teil}${sonst}.`);
  }

  if (a.flexibilitaet === "anpassen") {
    teile.push("Falls kein Produkt alle Vorgaben erfüllt, bin ich bereit, diese im Gespräch gemeinsam mit Ihnen anzupassen.");
  } else if (a.flexibilitaet === "verzichten") {
    teile.push("Falls kein Produkt alle Vorgaben erfüllt, verzichte ich lieber auf ein Investment, als Vorgaben zu lockern.");
  } else if (a.flexibilitaet === "unsicher") {
    teile.push("Wie ich mit Konflikten zwischen meinen Vorgaben und der Produktauswahl umgehe, möchte ich im Gespräch klären.");
  }

  return teile.join(" ");
}

function mapPaiLabel(key) {
  return CONFIG.paiThemen.find((t) => t.key === key)?.label || key;
}

// -------- Fragen-an-den-Berater-Generator --------
export function buildFragenAnBerater(a) {
  const q = [...CONFIG.kernfragen];
  a.werte.auswahl.forEach((k) => {
    const zusatz = CONFIG.themenfragen[k];
    if (zusatz) q.push(zusatz);
  });
  if (a.werte.sonstiges) {
    q.push(`Wie wird mein persönlicher Ausschlusswunsch „${a.werte.sonstiges}" in Ihren Produkten abgebildet oder dokumentiert?`);
  }
  return q;
}

// -------- Profil-Zusammenfassung (Kriterium → Vorgabe) --------
export function buildProfil(a) {
  const rows = [];
  rows.push({
    k: "Nachhaltigkeit gewünscht",
    v: a.grundfrage === "ja" ? "Ja" : a.grundfrage === "nein" ? "Nein" : "Unsicher – im Gespräch klären",
  });
  if (a.grundfrage === "nein") return rows;

  rows.push({
    k: "EU-Taxonomie (Mindestanteil)",
    v: a.taxonomie.active ? `mind. ${a.taxonomie.min} %` : "nicht wichtig",
  });
  rows.push({
    k: "Nachhaltige Investitionen (SFDR)",
    v: a.nachhaltige.active
      ? `mind. ${a.nachhaltige.min} %${a.nachhaltige.schwerpunkt === "oeko" ? " · Schwerpunkt ökologisch" : a.nachhaltige.schwerpunkt === "sozial" ? " · Schwerpunkt sozial" : ""}`
      : "nicht wichtig",
  });
  if (a.pai.active === "ja") {
    const themen = a.pai.themen.includes("alle")
      ? "alle wichtigen Themen"
      : a.pai.themen.map(mapPaiLabel).join(", ") || "unbestimmt";
    rows.push({ k: "PAI-Berücksichtigung", v: `Ja – ${themen}` });
  } else if (a.pai.active === "nein") {
    rows.push({ k: "PAI-Berücksichtigung", v: "Nein" });
  } else {
    rows.push({ k: "PAI-Berücksichtigung", v: "Unsicher – im Gespräch klären" });
  }
  const werteLabel = a.werte.auswahl
    .map((k) => CONFIG.ergaenzendeWerte.find((v) => v.key === k)?.label)
    .filter(Boolean);
  const werteText = [
    ...(werteLabel.length ? [werteLabel.join(", ")] : []),
    ...(a.werte.sonstiges ? [`Sonstiges: ${a.werte.sonstiges}`] : []),
  ].join(" · ");
  rows.push({ k: "Persönliche Ausschlüsse", v: werteText || "keine" });
  rows.push({
    k: "Flexibilität bei Konflikt",
    v: a.flexibilitaet === "anpassen"
      ? "Vorgaben im Gespräch anpassen"
      : a.flexibilitaet === "verzichten"
        ? "Lieber verzichten als lockern"
        : a.flexibilitaet === "unsicher"
          ? "Unsicher – im Gespräch klären"
          : "—",
  });
  return rows;
}
